import axios, { AxiosRequestConfig, AxiosPromise } from "axios";
import { makeUseAxios, RefetchOptions, ResponseValues, UseAxiosResult } from "axios-hooks";
import { acquireAccessToken } from "../auth/authConfig";

const axiosInstance = axios.create();
const useAxios = makeUseAxios({ axios: axiosInstance });

axiosInstance.interceptors.request.use(async (axiosConfig: AxiosRequestConfig) => {
	const token = await acquireAccessToken();
	axiosConfig.headers.common["Authorization"] = `Bearer ${token}`;
	return axiosConfig;
});

export type UseAxiosActionResult<TRequest = any, TResponse = any, TError = any> = [
	ResponseValues<TResponse, TError>,
	(requestData: TRequest, config?: AxiosRequestConfig, options?: RefetchOptions) => AxiosPromise<TResponse>,
	() => void,
	AxiosRequestConfig
];

/**
 * Axios hook for GET request. useAxiosGet(url|config, [manual]).
 * For axios-hooks documentation, please go to https://github.com/simoneb/axios-hooks
 * @remarks url|config - The request URL or config object, the same argument accepted by axios
 */
export function useAxiosGet<TResponse = any, TError = any>(
	config: string | AxiosRequestConfig,
	manual?: boolean
): UseAxiosResult<TResponse, TError> {
	const _config = getAxiosConfig(config, "GET");
	return useAxios<TResponse, TError>(_config, { manual, autoCancel: false });
}

/**
 * Axios hook for POST request. useAxiosPost(url|config)
 * For axios-hooks documentation, please go to https://github.com/simoneb/axios-hooks
 * @remarks url|config - The request URL or config object, the same argument accepted by axios
 */
export function useAxiosPost<TRequest = any, TResponse = any, TError = any>(
	config: string | AxiosRequestConfig
): UseAxiosActionResult<any, any, any> {
	return useAxiosAction<TRequest, TResponse, TError>(config, "POST");
}

/**
 * Axios hook for PUT request. useAxiosPut(url|config)
 * For axios-hooks documentation, please go to https://github.com/simoneb/axios-hooks
 * @remarks url|config - The request URL or config object, the same argument accepted by axios
 */
export function useAxiosPut<TRequest = any, TResponse = any, TError = any>(
	config: string | AxiosRequestConfig
): UseAxiosActionResult<any, any, any> {
	return useAxiosAction<TRequest, TResponse, TError>(config, "PUT");
}

/**
 * Axios hook for DELETE request. useAxiosDelete(url|config)
 * For axios-hooks documentation, please go to https://github.com/simoneb/axios-hooks
 * @remarks url|config - The request URL or config object, the same argument accepted by axios
 */
export function useAxiosDelete<TRequest = any, TResponse = any, TError = any>(
	config: string | AxiosRequestConfig
): UseAxiosActionResult<any, any, any> {
	return useAxiosAction<TRequest, TResponse, TError>(config, "DELETE");
}

/**
 * Axios hook for PATCH request. useAxiosDelete(url|config)
 * For axios-hooks documentation, please go to https://github.com/simoneb/axios-hooks
 * @remarks url|config - The request URL or config object, the same argument accepted by axios
 */
export function useAxiosPatch<TRequest = any, TResponse = any, TError = any>(
	config: string | AxiosRequestConfig
): UseAxiosActionResult<any, any, any> {
	return useAxiosAction<TRequest, TResponse, TError>(config, "PATCH");
}

function useAxiosAction<TRequest = any, TResponse = any, TError = any>(
	config: string | AxiosRequestConfig,
	method: "POST" | "PUT" | "DELETE" | "PATCH"
): UseAxiosActionResult<any, any, any> {
	const _config = getAxiosConfig(config, method);
	const [state, execute, manualCancel] = useAxios<TResponse, TError>(_config, {
		manual: true,
		autoCancel: false,
	});

	const actionAsync = async (model: TRequest, requestConfig?: AxiosRequestConfig) => {
		const requestModel = typeof model === "string" ? `"${model}"` : model;
		return await execute({ ...requestConfig, data: requestModel });
	};
	const result: UseAxiosActionResult = [state, actionAsync, manualCancel, _config];
	return result;
}

function getAxiosConfig(
	config: string | AxiosRequestConfig,
	method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
): AxiosRequestConfig {
	if (typeof config === "string") {
		return {
			url: config,
			method,
		};
	}
	config.method = method;
	return config;
}